/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Observable, from, Subject } from 'rxjs';
import { map, takeUntil, filter, mergeAll } from 'rxjs/operators';
import { FancyFirebaseList } from './FancyFirebaseList';
import { FancyAction } from './FancyAction';
import { FancyJoinRequirements } from './interfaces';
import { FancyFragmenter } from './FancyFragmenter';
import { fastSplice } from 'utils/basic-utils';
import { logger } from '@touchplan/logging';

// Literal interface for FancyIdJoin(er)
export interface IFancyIdJoin {
	applyJoinData: (baseKey: string, child: any, otherChild: any) => any;
	getDataForKey: (baseKey: string, child: any) => any;
	getPrevDataForKey?: (baseKey: string, child: any) => any;
}

//potential change, switch inline manipuation of base updates to write back onto the list like actions

/**
 * Use to join together multiple [[FancyFirebaseList]]'s.
 * Requires the baseList to implement the [[FancyJoinRequirements]] interface
 *
 * ```
 * var sourceList = RoleListFactory.create(args);
 * var joiner = new FancyIdJoin("roleId", sourceList);
 * var baseList = TicketListFactory.create(args, join);
 * ```
 */
export class FancyIdJoin<FancyBaseModel, FancySourceModel> {
	private shutdownSubject = new Subject<void>();
	/** the list with the ids */
	public baseList: FancyFirebaseList<FancyBaseModel, FancyAction> & FancyJoinRequirements<FancyBaseModel>;
	/** the cache containing the reverse references from sourceList to baseList (maybe use another nested map layer */
	public sourceToBaseCache = new Map<string, [string]>();
	public joinActionId: string;
	public ignoreMyself;

	/** a cache of prevSource id's per base child to be used basically just for change detection */
	private prevSourceIdForBaseIdCache = new Map<string, string>();

	/**
	 * @param baseKey    An id to key the join off of
	 * @param sourceList The firebase list containing the source data to be mapped into the base list
	 */
	constructor(
		private baseKey: string,
		private sourceList: FancyFirebaseList<FancySourceModel, FancyAction>,
		private fragmenter?: FancyFragmenter
	) {
		this.joinActionId = this.decorateActionId(baseKey);
		this.ignoreMyself = filter((action) => (<any>action).type !== this.joinActionId);
		// logger.log('joiner', this);
	}

	public decorateActionId(baseId) {
		return 'join_action_' + baseId;
	}

	//recieve the base list and setup subscriptions
	//
	//pass1: just pass in the whole damn thing
	//pass2: if possible pull back to just the internal list and the update functions
	public start(baseList) {
		this.baseList = baseList;
		// logger.log('start join', this.sourceList);
		const sourceObservable = this.handleSourceObserver(this.sourceList.safeRawEvent$());
		sourceObservable.subscribe((action) => {
			this.baseList.joinEvent$.next(action);
		});
	}

	/**
	 * the place where work is done, handles the firebase (or other?
	 * maybe just treat as updates?) events and builds/ maintains the cache
	 * and throws out new base events when changes are detected.
	 * On base these changes get forwarded out to manipBase of this and any other joiners.
	 * @param  obs essentially this.sourceObservable
	 * @return     Another observable to chain off of
	 */
	public handleSourceObserver(obs: Observable<FancyAction>) {
		return obs.pipe(
			this.ignoreMyself,
			map((action: FancyAction) => {
				const cacheList = this.sourceToBaseCache.get(action.key);

				// commenting this out for now, it seems... incorrect to do this
				// if(action.type === FancyActionTypes.childRemoved){
				// 	this.sourceToBaseCache.delete(action.key);
				// }
				// logger.log('source earlier', cacheList, action);
				return { cacheList, sourceAction: action };
			}),
			filter((enhancedAction) => enhancedAction.cacheList !== undefined && !!enhancedAction.cacheList.length),
			map((enhancedAction) => {
				// logger.log('join source obs');
				//TODO - there's probably a pure observable chain way to do this
				const newList =
					enhancedAction.cacheList.reduce((arr, key) => {
						if (this.baseList._internalList.has(key)) {
							const baseChild = this.baseList._internalList.get(key);
							if (baseChild) {
								// logger.log('source', this.baseKey, baseChild, enhancedAction.sourceAction.payload);
								//NEW THING REMOVED, MAY BREAK EVERYTHING
								this.baseList.applyJoinData(this.baseKey, baseChild, enhancedAction.sourceAction.payload);
								const action = new FancyAction(baseChild, this.joinActionId, key);
								arr.push(action);
							}
						} else {
							logger.log('was breaking here');
						}
						return arr;
					}, []) || [];
				return from(newList);
			}),
			mergeAll(),
			//not strictly necessary as long as the source destroys itself... but we shouldn't be assuming that here
			takeUntil(this.shutdownSubject)
		);
	}

	/**
	 * unnecessary update culling
	 */
	private hasIdChanged(action: FancyAction) {
		const baseChild = action.payload;
		const sourceId = this.baseList.getDataForKey(this.baseKey, baseChild);
		// const oldSourceId = this.baseList.getPrevDataForKey(this.baseKey, baseChild);
		const oldSourceId = this.getPrevSourceId(action.key, baseChild);
		const oldSourceChild = this.sourceList._internalList.get(oldSourceId);
		// return true;
		//it changed if the base was deleted
		if (!this.baseList._internalList.has(action.key) && this.sourceToBaseCache.has(oldSourceId)) {
			return true;
		}
		//it changed if the source was deleted
		if (oldSourceId && !this.sourceList._internalList.has(oldSourceId)) {
			return true;
		}

		return sourceId !== oldSourceId;
	}

	private updatePrevCache(action: FancyAction) {
		const baseId: string = action.key;
		const sourceId: string = this.baseList.getDataForKey(this.baseKey, action.payload);
		const isRemove = this.baseList.isRemoveAction(action);

		if (isRemove) {
			this.prevSourceIdForBaseIdCache.delete(baseId);
		} else {
			this.prevSourceIdForBaseIdCache.set(baseId, sourceId);
		}
	}

	/**
	 * Gets the cached prev sourceId from the base
	 * @param baseId The standard api, the id of the base item
	 * @param baseChild Legacy api, the base child itself. Avoid disturbing tickets mostly
	 */
	private getPrevSourceId(baseId: string, baseChild?: any) {
		let sourceId: string;
		//legacy path
		if (baseChild && this.baseList.getPrevDataForKey) {
			sourceId = this.baseList.getPrevDataForKey(this.baseKey, baseChild);
		}
		//standard path
		else {
			sourceId = this.prevSourceIdForBaseIdCache.get(baseId);
		}
		return sourceId;
	}

	/**
	 * Causes the base list to be manipulated inline with the source data without
	 * calling any additional events. Should likely only ever be called by the base
	 * [[FancyFirebaseList]]
	 */
	public manipBase(action: FancyAction) {
		if (this.baseList === null) {
			return;
		} //destroyed async stuff
		// if(action.type === this.joinActionId){ return; } //self referencing fix for the "placeholder list" concept
		// logger.log('all', action);
		if (!this.hasIdChanged(action)) {
			return;
		}
		// logger.log('handle me!', action);
		// if(action.key === "-float-1"){
		// 	debugger;
		// }
		//use action and getDataForKey, to get sourceId
		const baseChild = action.payload;
		const sourceId = this.baseList.getDataForKey(this.baseKey, baseChild);
		//remove from cache if the removed type
		// if(action.type === FancyActionTypes.childRemoved){
		// if(this.baseRemovalActions[action.type]){
		if (this.baseList.isRemoveAction(action)) {
			this.removeFromCache(sourceId, action.key);
			//remove the fragmenter if the cache has been emptied
			if (this.fragmenter && this.fragmenter.has(sourceId) && !this.sourceToBaseCache.has(sourceId)) {
				this.fragmenter.remove(sourceId);
			}
			// return undefined;
		} else {
			const sourceChild = this.sourceList._internalList.get(sourceId);
			// const oldSourceId = this.baseList.getPrevDataForKey(this.baseKey, baseChild);
			const oldSourceId = this.getPrevSourceId(action.key, baseChild);
			const oldSourceChild = this.sourceList._internalList.get(oldSourceId);
			this.baseList.applyJoinData(this.baseKey, baseChild, sourceChild);

			if (oldSourceId) {
				this.removeFromCache(oldSourceId, action.key);
			}

			this.addToCache(sourceId, action.key);

			if (this.fragmenter) {
				//add the new one if it's not there
				if (sourceId && !this.fragmenter.has(sourceId)) {
					this.fragmenter.add(sourceId);
				}
				//remove it if that fails, and it's undefined (may not need this one)
				//and was previously there and if the cache is empty for that source value
				else if (!sourceChild && this.fragmenter.has(sourceId) && !this.sourceToBaseCache.has(sourceId)) {
					this.fragmenter.remove(sourceId);
				}
				//remove the old one
				if (!oldSourceChild && this.fragmenter.has(oldSourceId) && !this.sourceToBaseCache.has(oldSourceId)) {
					this.fragmenter.remove(oldSourceId);
				}
				//doing this a second time mainly so that there's a syncronous path (currently just for testing)
				// this.baseList.applyJoinData(this.baseKey, baseChild, this.sourceList._internalList.get(sourceId));
			}

			// if(sourceChild){
			// this.addToFragmenterIfNotThere(sourceId);
			// }

			// if(this.fragmenter && sourceId){
			// 	this.fragmenter.add(sourceId);
			// }
			// this.addToFragmenterIfNotThere(sourceId);
			//RIGHT HERE, this is why constraints are broken
			// if(sourceChild){
			// this.addToCache(sourceId, action.key);
			// }
		}

		this.updatePrevCache(action);
	}

	private addToFragmenterIfNotThere(sourceId) {
		if (!this.sourceToBaseCache.has(sourceId) && this.fragmenter) {
			this.fragmenter.add(sourceId);
		}
	}

	private addToCache(sourceId, baseId) {
		if (!sourceId || !baseId) {
			return;
		}
		if (!this.sourceToBaseCache.has(sourceId)) {
			this.sourceToBaseCache.set(sourceId, [baseId]);
			// if(this.fragmenter){ this.fragmenter.add(sourceId); }
		} else {
			this.sourceToBaseCache.get(sourceId).push(baseId);
		}
		// logger.log('add to cache', sourceId, baseId, this.sourceToBaseCache);
	}
	private removeFromCache(sourceId, baseId) {
		const arr = this.sourceToBaseCache.get(sourceId);
		if (!arr || !arr.length) {
			return;
		}
		const index = arr.indexOf(baseId);
		if (index === -1) {
			return;
		}
		fastSplice(arr, index, 1);
		if (!arr.length) {
			// if(this.fragmenter){ this.fragmenter.remove(sourceId); }
			this.sourceToBaseCache.delete(sourceId);
		}
		// logger.log('remove from cache', sourceId, baseId, this.sourceToBaseCache);
		//arr.splice(index, 1);
	}

	public destroy() {
		this.shutdownSubject.next();
		this.shutdownSubject.complete();
		this.sourceList = null;
		this.baseList = null;
		this.sourceToBaseCache = null;
		if (this.fragmenter) {
			this.fragmenter.destroy();
			this.fragmenter = null;
		}
	}
}
